<template>
  <div
    v-if="reminders.length"
    class="follow-up-reminders"
    aria-live="polite"
  >
    <div
      v-for="reminder in reminders"
      :key="reminder.id"
      class="alert alert-warning follow-up-reminder"
    >
      <span>
        Scheduled follow-up with {{ reminder.firstName?.trim() || reminder.phoneNumber }} at
        {{ formatFollowUpTime(reminder.followUpAt) }} ET. Call by
        <strong>{{ formatCallByTime(reminder.followUpAt) }} ET.</strong>
      </span>
      <div>
        <p-button
          type="purple"
          round
          :pressed="true"
          @click="loadProfile(reminder)"
        >
          Load profile
        </p-button>
        <p-button
          v-if="!util.isProdMode"
          type="danger"
          round
          
          @click="removeFollowUp(reminder)"
        >
          Remove
        </p-button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import moment from 'moment-timezone';
import { onBeforeUnmount, onMounted, ref } from 'vue';

import { defaultTimeZone } from '@/constants/app';
import events from '@/events';
import type { FollowUpRequestsQuery } from '@/gql/graphql';
import { currentCallLog } from '@/services/callLogs/callLogsViewModel';
import { followUpRequestService } from '@/services/followUpRequest';
import util from '@/Utils/util';
import { log } from '@/Utils/logger';
import { useGlobal } from '@/use/global';
import { useProfile } from '@/use/profile';
import { useSwalDialog } from '@/use/swalDialog';
import { deleteFollowUpRequest } from '@/services/followUpRequest/deleteFollowUpRequest';

const pollingIntervalMs = 60 * 1000;

const { currentUser } = useProfile();
const { isOnActiveCall } = useGlobal();
const { showConfirmPopupDialog } = useSwalDialog();

type FollowUpReminder = FollowUpRequestsQuery['followUpRequests']['rows'][number];
const reminders = ref<FollowUpReminder[]>([]);
const dismissedIds = new Set<number>();
const operatorId = currentUser.value.id;

let pollingInterval: ReturnType<typeof setInterval> | undefined;
let refreshGeneration = 0;

const refreshReminders = async () => {
  const requestGeneration = ++refreshGeneration;
  try {
    const response = await followUpRequestService.fetchFollowUpRequests({
      dueWithinMinutes: 5,
      pagination: null,
    });
    if (requestGeneration !== refreshGeneration) {
      return;
    }

    const rows = response?.rows ?? [];
    reminders.value = rows.filter(({ id }) => !dismissedIds.has(id));
  } catch (error) {
    log.warn(
      {
        func: 'FollowUpReminderBanner.refreshReminders',
        operatorId,
        error,
      },
      'Failed to refresh follow-up reminders',
    );
  }
};

const stopPolling = () => {
  refreshGeneration += 1;
  if (pollingInterval) {
    clearInterval(pollingInterval);
    pollingInterval = undefined;
  }
};

const startPolling = () => {
  void refreshReminders();
  pollingInterval = setInterval(() => void refreshReminders(), pollingIntervalMs);
};

const formatTime = (date: string, subtractMinutes = 0) => {
  const time = moment.tz(date, defaultTimeZone).subtract(subtractMinutes, 'minute');
  return time.minutes() === 0 ? time.format('h A') : time.format('h:mm A');
};

const formatFollowUpTime = (date: string) => formatTime(date);
const formatCallByTime = (date: string) => formatTime(date, 1);

const comparablePhoneNumber = (phoneNumber?: string | null) => phoneNumber?.replace(/\D/g, '') ?? '';

const isDifferentActiveCaller = (reminder: FollowUpReminder) => {
  if (!isOnActiveCall.value || !currentCallLog.value) {
    return false;
  }

  if (reminder.callerId && currentCallLog.value.callerId) {
    return reminder.callerId !== currentCallLog.value.callerId;
  }

  const activeCallPhoneNumber = comparablePhoneNumber(util.getCallingPhoneNumber(currentCallLog.value));
  return Boolean(activeCallPhoneNumber && activeCallPhoneNumber !== comparablePhoneNumber(reminder.phoneNumber));
};

const loadProfile = async (reminder: FollowUpReminder) => {
  if (isDifferentActiveCaller(reminder)) {
    const isConfirmed = await showConfirmPopupDialog({
      title: 'Load a different profile?',
      text: 'You are currently on a call with a different caller. Your active call will not be interrupted.',
      confirmButtonText: 'Load profile',
      cancelButtonText: 'Cancel',
    });

    if (!isConfirmed) {
      return;
    }
  }

  events.emit('refresh-right-panel', { phone: reminder.phoneNumber });
};

const removeFollowUp = (reminder: FollowUpReminder) => {
  deleteFollowUpRequest(reminder.id);
  refreshReminders();
}

const dismissReminder = (followUpRequestId: number) => {
  dismissedIds.add(followUpRequestId);
  reminders.value = reminders.value.filter(({ id }) => id !== followUpRequestId);
};

onMounted(() => {
  events.on('follow-up-call-started', dismissReminder);
  startPolling();
});

onBeforeUnmount(() => {
  stopPolling();
  events.off('follow-up-call-started', dismissReminder);
});
</script>

<style scoped lang="scss">
.follow-up-reminders {
  position: sticky;
  top: 0;
  z-index: 10;
}

.follow-up-reminder {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 10px;
  padding: 12px 16px;
  color: black;

  .btn {
    flex-shrink: 0;
    margin: 0;
  }
}

@media (max-width: 768px) {
  .follow-up-reminder {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
